import { Subject } from 'rxjs'

import { EntityType, EntitySynchroResult } from '@skinsoft/types';
import { ImageTransferHelper, MediaTransferHelper } from "@skinsoft/media-transfer-helper";
import { SynchroUpIdMapping } from '@skinsoft/synchro-up-mapping';

declare const config
const MAX_TRY_WITHOUT_RESULT: number = config.syncUp.medias.maxNbRetry
const DELAY_BETWEEN_FILE_SEARCH: number = config.syncUp.medias.delayBetweenRetry

export class MediaUploadHandler {

    private filenameCacheKeyMap: { [filename: string]: string }
    private uploadResultSubject: Subject<EntitySynchroResult>
    private fileNames: string[]
    private nbTryWithoutResult: number
    private nbLeftToUpload: number

    private synchroUpIdMapping: SynchroUpIdMapping = null as any

    private getCacheMediaToUpload(): Promise<string[]> {
        return ImageTransferHelper.getInstance().getCachedImagesKeys()
    }

    private init(uploadResultSubject: Subject<EntitySynchroResult>) {
        this.uploadResultSubject = uploadResultSubject
        this.filenameCacheKeyMap = {}
        this.fileNames = []
        this.nbTryWithoutResult = 0
        this.nbLeftToUpload = 0
        this.synchroUpIdMapping = SynchroUpIdMapping.getInstance()
    }

    public async run(uploadResultSubject: Subject<EntitySynchroResult>) {
        console.debug("Start cached media uploader...")
        this.init(uploadResultSubject)

        const cachedKeys = await this.getCacheMediaToUpload()
        this.nbLeftToUpload = cachedKeys.length
        console.debug("Cached medias to upload: ", cachedKeys)

        console.debug("Start upload of cached medias")
        const imageTransferHelper = ImageTransferHelper.getInstance()
        for(const cachedMediaId of cachedKeys) {
            console.debug(`Start upload of media ${cachedMediaId}`)
            imageTransferHelper.uploadLocalImageToApi(cachedMediaId, true).subscribe(async uploadResult => {
                if(uploadResult.fileName) {
                    this.fileNames.push(uploadResult.fileName)
                    this.filenameCacheKeyMap[uploadResult.fileName] = cachedMediaId
                    console.debug(`Upload of media ${cachedMediaId} succeded, filename: ${uploadResult.fileName}`)
                } else {
                    console.error(`Upload of media ${cachedMediaId} failed`)
                    this.uploadResultSubject.next({
                        success: false, 
                        entityId: cachedMediaId,
                        entityType: EntityType.MEDIA,
                    })
                    this.nbLeftToUpload--
                }
            })
        }

        console.debug("Start retrieval of uploaded medias ids...")
        this.findUploadedMediasIds()
    }

    private findUploadedMediasIds() {
        const theRealThis = this
        MediaTransferHelper.getInstance().findMediaIdsFromFileNames(this.fileNames).subscribe(async medias => {
            if(medias.length) {
                for(const media of medias) {
                    const fileName = media.metaData?.fileRecordCS?.fileName
                    if(fileName) {
                        const cacheKey = theRealThis.filenameCacheKeyMap[fileName]
                        // put id in map
                        if(cacheKey) {
                            console.debug(`Media ${cacheKey} uploaded id: ${media.id}`)
                            await this.synchroUpIdMapping.setNewId(cacheKey, media.id)

                            theRealThis.fileNames = theRealThis.fileNames.filter(name => {
                                return name !== fileName 
                            })
                            theRealThis.uploadResultSubject.next({
                                success: true, 
                                entityId: cacheKey,
                                entityType: EntityType.MEDIA,
                            })
                            theRealThis.nbLeftToUpload--
                        }
                    }
                }
            } else {
                theRealThis.nbTryWithoutResult++
            }

            if(theRealThis.nbLeftToUpload && theRealThis.nbTryWithoutResult < MAX_TRY_WITHOUT_RESULT) {
                setTimeout(
                    () => {
                        theRealThis.findUploadedMediasIds()
                    }, 
                    DELAY_BETWEEN_FILE_SEARCH
                )
            } else {
                for(const fileName of theRealThis.fileNames) {
                    const cacheKey = theRealThis.filenameCacheKeyMap[fileName]
                    theRealThis.nbLeftToUpload = 0
                    if(cacheKey) {
                        theRealThis.uploadResultSubject.next({
                            success: false, 
                            entityId: cacheKey,
                            entityType: EntityType.MEDIA
                        })
                    }
                }
            }
        })
    }
}